"use client";

import React from "react";
import { Compass, Layers, ShieldCheck, Ruler, Hammer, Maximize2, TrendingUp, Milestone, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface DecisionItem {
  id: string;
  title: string;
  detail: string;
  icon: React.ElementType;
}
const designDecisions: DecisionItem[] = [{
  id: "material-selection",
  title: "Decking material: composite vs. natural wood",
  detail: "Capped polymer or composite boards minimize seasonal maintenance and resist splintering, while traditional pressure-treated pine or cedar offers authentic grain with scheduled staining cycles.",
  icon: Layers
}, {
  id: "structural-framing",
  title: "Structural design & load requirements",
  detail: "Joist spacing, cantilever spans, ledger board attachment, and beam sizing ensure full structural integrity under live loads and Georgia code standards.",
  icon: Hammer
}, {
  id: "railing-systems",
  title: "Railing style & infill profiles",
  detail: "Choose between traditional wood balusters, modern horizontal cable rail, contemporary aluminum bars, or transparent tempered glass panels to preserve yard views.",
  icon: ShieldCheck
}, {
  id: "layout-levels",
  title: "Multi-level & functional zoning",
  detail: "Stepped transitions separate open-air grilling stations, dining lounges, and covered sitting nooks, optimizing traffic flow from interior thresholds to the yard.",
  icon: Compass
}];
const costDrivers: DecisionItem[] = [{
  id: "total-footprint",
  title: "Total square footage & perimeter lines",
  detail: "Overall platform area directly dictates framing volume, decking counts, footings, and trim requirements, with angled corners adding custom mitering time.",
  icon: Maximize2
}, {
  id: "material-tier",
  title: "Material tier and finish selection",
  detail: "Entry-level pressure-treated framing and decking versus premium multi-chromatic composite boards, hidden fastening hardware, and matching fascia wraps.",
  icon: TrendingUp
}, {
  id: "substructure-health",
  title: "Existing deck framing condition",
  detail: "Determining whether existing support posts and framing can be brought to code and re-decked, or if full demolition and replacement of footings is required.",
  icon: Ruler
}, {
  id: "stairs-and-railings",
  title: "Railing and stair run complexity",
  detail: "Linear footage of safety railings, continuous handrails, wraparound steps, and integrated low-voltage step risers require precise craft and hardware.",
  icon: Milestone
}, {
  id: "grading-foundations",
  title: "Grading and foundation requirements",
  detail: "Sloped Georgia terrain, soil stability, drainage management, and frost-depth concrete pier footings define the structural anchor below the surface.",
  icon: Layers
}];
export default function DeckPlanningDecisions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="deckplanningdecisions" data-nav="light" ref={ref} className={`relative w-full bg-background text-foreground py-20 lg:py-28 transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-16 lg:mb-20">
          <p className="text-sm font-semibold tracking-wider uppercase text-primary mb-3">
            Project Architecture & Scoping
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-foreground text-balance">
            Key planning decisions for your outdoor living space
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every deck renovation in Brookhaven balances aesthetic vision with architectural requirements. Review the primary design choices and structural cost drivers that shape your build before breaking ground.
          </p>
        </div>

        {/* Dual Column Layout: Design Choices vs Cost Drivers */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16">
          {/* Column 1: Key Design & Material Decisions */}
          <div className="lg:col-span-6 flex flex-col gap-6">
            <Card className="border border-border/80 shadow-sm bg-card hover:shadow-md transition-shadow">
              <CardHeader className="pb-4">
                <div className="inline-flex items-center gap-2 text-primary font-medium text-xs tracking-wider uppercase mb-1">
                  <Compass className="size-4" />
                  <span>Architecture & Finishes</span>
                </div>
                <CardTitle className="text-2xl font-serif font-semibold text-card-foreground">
                  Core design & material decisions
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Fundamental aesthetic and structural choices that determine how your deck looks, handles weathering, and connects to your daily life.
                </p>
              </CardHeader>
              <CardContent className="pt-2">
                <ul className="space-y-6">
                  {designDecisions.map((item, i) => {
                  const IconComponent = item.icon;
                  return <li key={item.id} data-index={i} className="flex items-start gap-4">
                        <div className="mt-1 flex size-8 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground border border-border">
                          <IconComponent className="size-4 text-primary" />
                        </div>
                        <div className="space-y-1">
                          <h3 className="text-base font-semibold text-foreground leading-snug">
                            {item.title}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {item.detail}
                          </p>
                        </div>
                      </li>;
                })}
                </ul>
              </CardContent>
            </Card>

            {/* Supporting Photo 1 */}
            <div className="overflow-hidden rounded-xl border border-border shadow-xs">
              <AspectRatio ratio={16 / 9}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/composite_deck_lounging_area.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/composite_deck_lounging_area.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/composite_deck_lounging_area.png-wvc-sizes" alt="Multi-level residential composite deck with integrated railings and seating area" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </AspectRatio>
              <div className="p-4 bg-muted/40 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Custom multi-tier composite deck featuring low-profile drink rails and flush transition thresholds.
                </p>
              </div>
            </div>
          </div>

          {/* Column 2: Cost-Driving Factors */}
          <div className="lg:col-span-6 flex flex-col gap-6">
            <Card className="border border-border/80 shadow-sm bg-card hover:shadow-md transition-shadow">
              <CardHeader className="pb-4">
                <div className="inline-flex items-center gap-2 text-primary font-medium text-xs tracking-wider uppercase mb-1">
                  <TrendingUp className="size-4" />
                  <span>Site & Scope Realities</span>
                </div>
                <CardTitle className="text-2xl font-serif font-semibold text-card-foreground">
                  Key cost-driving factors
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Physical and engineering parameters that determine crew labor, structural reinforcement, and overall project duration.
                </p>
              </CardHeader>
              <CardContent className="pt-2">
                <ul className="space-y-6">
                  {costDrivers.map((item, i) => {
                  const IconComponent = item.icon;
                  return <li key={item.id} data-index={i} className="flex items-start gap-4">
                        <div className="mt-1 flex size-8 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground border border-border">
                          <IconComponent className="size-4 text-primary" />
                        </div>
                        <div className="space-y-1">
                          <h3 className="text-base font-semibold text-foreground leading-snug">
                            {item.title}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {item.detail}
                          </p>
                        </div>
                      </li>;
                })}
                </ul>
              </CardContent>
            </Card>

            {/* Supporting Photo 2 */}
            <div className="overflow-hidden rounded-xl border border-border shadow-xs">
              <AspectRatio ratio={16 / 9}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_substructure_and_framing_reinforcement.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_substructure_and_framing_reinforcement.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_substructure_and_framing_reinforcement.png-wvc-sizes" alt="Precision timber framing and concrete pier foundations for an elevated outdoor deck" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </AspectRatio>
              <div className="p-4 bg-muted/40 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Engineered post footings, ledger bolting, and joist spans built to accommodate steep rear property elevations.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Callout Bar */}
        <div className="rounded-xl border border-border bg-card p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 shadow-xs">
          <div className="space-y-1 max-w-xl">
            <h3 className="text-lg font-serif font-semibold text-foreground">
              Ready to evaluate your property’s deck potential?
            </h3>
            <p className="text-sm text-muted-foreground">
              We inspect existing substructures, verify elevation footings, and present tailored material samples for Brookhaven homes.
            </p>
          </div>
          <Button asChild size="lg" className="w-full sm:w-auto">
            <Link to="/ContactRequestAnEstimatePage">
              Request an in-person estimate
              <ArrowRight className="size-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}